import { env } from '@/config/env';
import {
  ANALYTICS_CONSENT_COOKIE,
  areAnalyticsCookiesAllowed,
} from '@/lib/analytics/consent';

export const GOOGLE_TAG_MANAGER_CONTAINER_ID =
  env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;

export const GOOGLE_TAG_MANAGER_CONSENT_SCRIPT = GOOGLE_TAG_MANAGER_CONTAINER_ID
  ? `\
(function(w,d){
w.dataLayer=w.dataLayer||[];
w.gtag=function(){w.dataLayer.push(arguments);};
var hasAnalyticsConsent=d.cookie.split(';').some(function(cookie){
return cookie.trim()==='${ANALYTICS_CONSENT_COOKIE}=true';
});
var consentValue=hasAnalyticsConsent?'granted':'denied';
w.gtag('consent','default',{
ad_storage:consentValue,
analytics_storage:consentValue,
ad_user_data:consentValue,
ad_personalization:consentValue,
functionality_storage:'granted',
security_storage:'granted'
});
})(window,document);`
  : undefined;

export const GOOGLE_TAG_MANAGER_SCRIPT = GOOGLE_TAG_MANAGER_CONTAINER_ID
  ? `\
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GOOGLE_TAG_MANAGER_CONTAINER_ID}');`
  : undefined;

type GoogleTagManagerSignupMethod = 'email' | 'google' | 'microsoft';
type GoogleTagManagerSignupPlan = 'free' | 'pro' | 'enterprise';
type GoogleTagManagerBillingCycle = 'monthly' | 'yearly';

type GoogleTagManagerSignupEventInput = {
  value: number;
  currency?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  signupMethod?: GoogleTagManagerSignupMethod | null;
  planSelected?: GoogleTagManagerSignupPlan | null;
  billingCycle?: GoogleTagManagerBillingCycle | null;
};

const SIGNUP_PLAN_LABELS = {
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
} satisfies Record<GoogleTagManagerSignupPlan, string>;

export function getGoogleTagManagerConsentState(analyticsAllowed: boolean) {
  const consentValue = analyticsAllowed ? 'granted' : 'denied';

  return {
    ad_storage: consentValue,
    analytics_storage: consentValue,
    ad_user_data: consentValue,
    ad_personalization: consentValue,
  };
}

function removeEmptyValues(event: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(event).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value.trim() === '') return false;

      return true;
    }),
  );
}

export function buildGoogleTagManagerSignupEvent(
  input: GoogleTagManagerSignupEventInput,
) {
  return removeEmptyValues({
    event: 'sign_up',
    value: input.value,
    currency: input.currency ?? 'USD',
    first_name: input.firstName?.trim(),
    last_name: input.lastName?.trim(),
    email: input.email?.trim(),
    phone: input.phone?.trim(),
    company_name: input.companyName?.trim(),
    signup_method: input.signupMethod,
    plan_selected: input.planSelected
      ? SIGNUP_PLAN_LABELS[input.planSelected]
      : undefined,
    billing_cycle: input.billingCycle,
  });
}

export function trackGoogleTagManagerEvent(event: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  if (!areAnalyticsCookiesAllowed()) return;

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(removeEmptyValues(event));
}
